import React, { useEffect, useState } from "react";
import { api, API_BASE } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { DownloadSimple } from "@phosphor-icons/react";

function downloadCsv(path) {
  const a = document.createElement("a");
  a.href = `${API_BASE}${path}`;
  a.target = "_blank";
  a.rel = "noopener noreferrer";
  a.click();
}

export function AdminSignups() {
  const [rows, setRows] = useState([]);
  const [q, setQ] = useState("");
  const load = () => api.get(`/admin/signups${q ? `?q=${encodeURIComponent(q)}` : ""}`).then((r) => setRows(r.data));
  // load is reconstructed each render; q in dep array is the only true dependency.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, [q]);
  return (
    <Page title="Signups" no="01" csvPath="/admin/signups.csv" csvName="signups.csv"
      filters={<Input placeholder="Search email / name / business…" value={q}
        onChange={(e) => setQ(e.target.value)} className="max-w-xs" data-testid="signups-search" />}>
      <Table>
        <TableHeader>
          <TableRow className="border-b border-ink">
            <TableHead className="label-mono">Email</TableHead>
            <TableHead className="label-mono">Name</TableHead>
            <TableHead className="label-mono">Business</TableHead>
            <TableHead className="label-mono">Marketplace</TableHead>
            <TableHead className="label-mono">Revenue</TableHead>
            <TableHead className="label-mono">Provider</TableHead>
            <TableHead className="label-mono">Role</TableHead>
            <TableHead className="label-mono">Verified</TableHead>
            <TableHead className="label-mono">Created</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {rows.map((r) => (
            <TableRow key={r.id} data-testid={`signup-row-${r.id}`} className="hover:bg-surface-hover">
              <TableCell className="font-medium">{r.email}</TableCell>
              <TableCell>{r.name || "—"}</TableCell>
              <TableCell>{r.business_name || "—"}</TableCell>
              <TableCell>{r.marketplace || "—"}</TableCell>
              <TableCell>{r.monthly_revenue_band || "—"}</TableCell>
              <TableCell><Badge variant="outline">{r.auth_provider}</Badge></TableCell>
              <TableCell><Badge variant={r.role === "admin" ? "default" : "outline"}>{r.role}</Badge></TableCell>
              <TableCell>{r.verified ? "✓" : "—"}</TableCell>
              <TableCell className="font-mono tabular text-xs text-ink/60">{(r.created_at || "").slice(0, 10)}</TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </Page>
  );
}

export function AdminLeads() {
  const [rows, setRows] = useState([]);
  const load = () => api.get("/admin/leads").then((r) => setRows(r.data));
  useEffect(() => { load(); }, []);
  const toggle = async (l) => {
    const next = l.status === "new" ? "contacted" : "new";
    await api.patch(`/admin/leads/${l.id}`, { status: next });
    load();
  };
  return (
    <Page title="Leads · Help us increase your business" no="02"
      csvPath="/admin/leads.csv" csvName="leads.csv">
      <Table>
        <TableHeader>
          <TableRow className="border-b border-ink">
            <TableHead className="label-mono">Name</TableHead>
            <TableHead className="label-mono">Email</TableHead>
            <TableHead className="label-mono">Phone</TableHead>
            <TableHead className="label-mono">Type</TableHead>
            <TableHead className="label-mono">Message</TableHead>
            <TableHead className="label-mono">Source</TableHead>
            <TableHead className="label-mono">Status</TableHead>
            <TableHead className="label-mono">Created</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {rows.map((r) => (
            <TableRow key={r.id} data-testid={`lead-row-${r.id}`} className="hover:bg-surface-hover">
              <TableCell className="font-medium">{r.name}</TableCell>
              <TableCell>{r.email}</TableCell>
              <TableCell>{r.phone || "—"}</TableCell>
              <TableCell><Badge variant="outline">{r.type}</Badge></TableCell>
              <TableCell className="max-w-xs truncate text-ink/70" title={r.message}>{r.message || "—"}</TableCell>
              <TableCell className="label-mono">{r.source_page}</TableCell>
              <TableCell>
                <button onClick={() => toggle(r)} data-testid={`lead-toggle-${r.id}`}>
                  <Badge variant="outline"
                    className={r.status === "contacted" ? "border-signal/40 text-signal" : "border-gold/40 text-gold"}>
                    {r.status}
                  </Badge>
                </button>
              </TableCell>
              <TableCell className="font-mono tabular text-xs text-ink/60">{(r.created_at || "").slice(0, 10)}</TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </Page>
  );
}

export function AdminWaitlist() {
  const [rows, setRows] = useState([]);
  useEffect(() => { api.get("/admin/waitlist").then((r) => setRows(r.data)); }, []);
  return (
    <Page title="Waitlist" no="03" csvPath="/admin/waitlist.csv" csvName="waitlist.csv">
      <Table>
        <TableHeader>
          <TableRow className="border-b border-ink">
            <TableHead className="label-mono">#</TableHead>
            <TableHead className="label-mono">Email</TableHead>
            <TableHead className="label-mono">Name</TableHead>
            <TableHead className="label-mono">Business</TableHead>
            <TableHead className="label-mono">Marketplace</TableHead>
            <TableHead className="label-mono">Revenue</TableHead>
            <TableHead className="label-mono">Created</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {rows.map((r) => (
            <TableRow key={r.id} className="hover:bg-surface-hover">
              <TableCell className="font-mono tabular font-medium">#{r.position}</TableCell>
              <TableCell className="font-medium">{r.email}</TableCell>
              <TableCell>{r.name || "—"}</TableCell>
              <TableCell>{r.business_name || "—"}</TableCell>
              <TableCell>{r.marketplace || "—"}</TableCell>
              <TableCell>{r.monthly_revenue_band || "—"}</TableCell>
              <TableCell className="font-mono tabular text-xs text-ink/60">{(r.created_at || "").slice(0, 10)}</TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </Page>
  );
}

function Page({ title, no, csvPath, csvName, filters, children }) {
  return (
    <div>
      <div className="border-b border-ink pb-8">
        <div className="flex items-end justify-between gap-3 flex-wrap">
          <div>
            <div className="label-mono">Admin · № {no}</div>
            <h1 className="mt-3 font-display font-black text-4xl md:text-5xl tracking-tightest text-ink leading-[1]"
              data-testid={`admin-page-${title.slice(0, 8)}`}>{title}.</h1>
          </div>
          <div className="flex gap-2 items-center">
            {filters}
            {csvPath && (
              <Button variant="outline" onClick={() => downloadCsv(csvPath)} data-testid={`csv-${csvName}`}>
                <DownloadSimple className="w-4 h-4 mr-1.5" /> Download CSV
              </Button>
            )}
          </div>
        </div>
      </div>
      <div className="mt-8 border border-rule bg-white overflow-x-auto">{children}</div>
    </div>
  );
}
